
import { computed, toRefs } from 'vue'

import Close from 'vue-material-design-icons/Close.vue'
import PencilIcon from 'vue-material-design-icons/Pencil.vue'

import { t } from '@nextcloud/l10n'

import NcButton from '@nextcloud/vue/dist/Components/NcButton.js'
import NcRichText from '@nextcloud/vue/dist/Components/NcRichText.js'

import AvatarWrapper from './AvatarWrapper/AvatarWrapper.vue'
import DefaultParameter from './MessagesList/MessagesGroup/Message/MessagePart/DefaultParameter.vue'
import FilePreview from './MessagesList/MessagesGroup/Message/MessagePart/FilePreview.vue'

import { useMessageInfo } from '../composables/useMessageInfo.js'
import { ATTENDEE, AVATAR } from '../constants.ts'
import { EventBus } from '../services/EventBus.ts'
import { useChatExtrasStore } from '../stores/chatExtras.js'

export default {
	name: 'Quote',

	components: {
		AvatarWrapper,
		NcButton,
		NcRichText,
		// Icons
		Close,
		PencilIcon,
	},

	props: {
		// The quoted message object
		message: {
			type: Object,
			required: true,
		},
		// Whether to show remove / cancel action
		canCancel: {
			type: Boolean,
			default: false,
		},
		// Whether to show edit actions
		editMessage: {
			type: Boolean,
			default: false,
		},
	},

	setup(props) {
		const { message } = toRefs(props)
		const chatExtrasStore = useChatExtrasStore()
		const {
			isFileShare,
			isFileShareWithoutCaption,
			remoteServer,
			lastEditor,
			actorDisplayName,
			actorDisplayNameWithFallback,
		} = useMessageInfo(message)

		const actorInfo = computed(() => {
			return [actorDisplayNameWithFallback.value, remoteServer.value, lastEditor.value]
				.filter(value => value).join(' ')
		})

		return {
			AVATAR,
			chatExtrasStore,
			isFileShare,
			isFileShareWithoutCaption,
			actorDisplayName,
			actorInfo,
		}
	},

	computed: {
		component() {
			return this.canCancel
				? { tag: 'div', link: undefined }
				: { tag: 'router-link', link: { hash: this.hash, params: { skipLeaveWarning: true } } }
		},

		isOwnMessageQuoted() {
			return this.message.actorId === this.$store.getters.getActorId()
				&& this.message.actorType === this.$store.getters.getActorType()
		},

		richParameters() {
			const richParameters = {}
			Object.keys(this.message.messageParameters).forEach(function(p) {
				const type = this.message.messageParameters[p].type
				if (type === 'file') {
					richParameters[p] = {
						component: FilePreview,
						props: {
							token: this.message.token,
							smallPreview: true,
							rowLayout: !this.message.messageParameters[p].mimetype.startsWith('image/'),
							file: this.message.messageParameters[p],
						},
					}
				} else {
					richParameters[p] = {
						component: DefaultParameter,
						props: this.message.messageParameters[p],
					}
				}
			}.bind(this))
			return richParameters
		},

		/**
		 * This is a simplified version of the last chat message.
		 * Parameters are parsed without markup (just replaced with the name),
		 * e.g. no avatars on mentions.
		 *
		 * @return {string} A simple message to show below the conversation name
		 */
		simpleQuotedMessage() {
			if (!this.message.id) {
				return t('spreed', 'The message has expired or has been deleted')
			}

			if (!Object.keys(this.message.messageParameters).length) {
				return this.message.message
			}

			const params = this.message.messageParameters
			let subtitle = this.message.message

			// We don't really use rich objects in the subtitle, instead we fall back to the name of the item
			Object.keys(params).forEach((parameterKey) => {
				subtitle = subtitle.replaceAll('{' + parameterKey + '}', params[parameterKey].name)
			})

			return subtitle
		},

		/**
		 * Shorten the message to 250 characters and append three dots to the end of the
		 * string. This is needed because on very wide screens, if the 250 characters
		 * fit, the css rules won't ellipsize the text-overflow.
		 */
		shortenedQuoteMessage() {
			return this.simpleQuotedMessage.length >= 250
				? this.simpleQuotedMessage.substring(0, 250) + '…'
				: this.simpleQuotedMessage
		},

		cancelQuoteLabel() {
			return t('spreed', 'Cancel quote')
		},

		hash() {
			return '#message_' + this.message.id
		},
	},

	methods: {
		t,
		handleAbort() {
			if (this.editMessage) {
				this.chatExtrasStore.removeMessageIdToEdit(this.message.token)
			} else {
				this.chatExtrasStore.removeParentIdToReply(this.message.token)
			}
			EventBus.emit('focus-chat-input')
		},

		handleQuoteClick() {
			if (this.canCancel) {
				return
			}

			if (this.$route.hash === this.hash) {
				// Already on this message route, just trigger highlight
				EventBus.emit('focus-message', this.message.id)
			}
		},
	},
}
